// src/components/PublicCompetitionView.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Tabs,
    Tab,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContentText
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getCompetitionDetails, getUserRegistrations, getCompetitionJudges, addCompetitionJudge } from '../api/competitionApi';
import CompetitionInfo from './CompetitionInfo';
import CompetitionWorkouts from './CompetitionWorkouts';
import CompetitionLeaderboard from './CompetitionLeaderboard';
import CompetitionSchedule from './CompetitionSchedule';
import RegisterCompetitionDialog from './RegisterCompetitionDialog';
import SubmitScoreDialog from './SubmitScoreDialog';

export default function PublicCompetitionView({ competitionId, userId, onClose }) {
    const [competition, setCompetition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [userRegistration, setUserRegistration] = useState(null);
    const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
    const [openSubmitScoreDialog, setOpenSubmitScoreDialog] = useState(false);
    const [isUserJudge, setIsUserJudge] = useState(false);
    const [registeringAsJudge, setRegisteringAsJudge] = useState(false);
    const [confirmJudgeDialogOpen, setConfirmJudgeDialogOpen] = useState(false);

    useEffect(() => {
        loadCompetition();
        checkUserRegistration();
        checkIfUserIsJudge();
    }, [competitionId, userId]);

    const loadCompetition = async () => {
        try {
            setLoading(true);
            const data = await getCompetitionDetails(competitionId);
            setCompetition(data);
        } catch (error) {
            console.error('Error loading competition:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkUserRegistration = async () => {
        try {
            const registrations = await getUserRegistrations(userId);
            const registration = registrations.find(
                r => r.competitionId === parseInt(competitionId)
            );
            setUserRegistration(registration);
        } catch (error) {
            console.error('Error checking registration:', error);
        }
    };

    const checkIfUserIsJudge = async () => {
        try {
            if (!userId) return;
            const judges = await getCompetitionJudges(competitionId);
            const userIsJudge = judges.some(judge => judge.user?.id === userId || judge.userId === userId);
            setIsUserJudge(userIsJudge);
        } catch (error) {
            console.error('Error checking if user is judge:', error);
        }
    };

    const handleRegisterAsJudgeClick = () => {
        setConfirmJudgeDialogOpen(true);
    };

    const handleConfirmRegisterAsJudge = async () => {
        setConfirmJudgeDialogOpen(false);
        try {
            setRegisteringAsJudge(true);
            await addCompetitionJudge(competitionId, userId);
            await checkIfUserIsJudge();
            await loadCompetition();
        } catch (error) {
            console.error('Error registering as judge:', error);
            alert(error.response?.data?.error || 'Failed to register as judge');
        } finally {
            setRegisteringAsJudge(false);
        }
    };

    const handleCancelRegisterAsJudge = () => {
        setConfirmJudgeDialogOpen(false);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleRegisterSuccess = () => {
        setOpenRegisterDialog(false);
        checkUserRegistration();
        loadCompetition();
    };

    const handleSubmitScoreSuccess = () => {
        setOpenSubmitScoreDialog(false);
        loadCompetition();
    };

    if (loading) {
        return (
            <DialogContent>
                <Typography>Loading competition...</Typography>
            </DialogContent>
        );
    }

    if (!competition) {
        return (
            <DialogContent>
                <Typography>Competition not found</Typography>
            </DialogContent>
        );
    }

    const isRegistrationOpen = new Date(competition.endDate) > new Date();
    const canSubmitScores = competition.allowSelfScoring && userRegistration;
    const canRegisterAsJudge = competition.allowJudgeSelfRegistration && !isUserJudge && isRegistrationOpen;

    return (
        <>
            <DialogTitle sx={{ pb: 1, position: 'relative', pr: 6 }}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8
                    }}
                >
                    <Close />
                </IconButton>

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2
                }}>
                    <Typography variant="h6" sx={{ flexGrow: { xs: 0, sm: 1 } }}>
                        {competition.name}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        width: { xs: '100%', sm: 'auto' },
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        {canSubmitScores && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenSubmitScoreDialog(true)}
                                size="large"
                                sx={{ 
                                    flex: { xs: 1, sm: 0 },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Add Score
                            </Button>
                        )}
                        {!userRegistration && isRegistrationOpen && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => setOpenRegisterDialog(true)}
                                size="small"
                                sx={{ 
                                    flex: { xs: 1, sm: 0 },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Register
                            </Button>
                        )}
                        {canRegisterAsJudge && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleRegisterAsJudgeClick}
                                disabled={registeringAsJudge}
                                size="small"
                                sx={{ 
                                    flex: { xs: 1, sm: 0 },
                                    width: { xs: '100%', sm: 'auto' },
                                    minWidth: { xs: 'auto', sm: '160px' },
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Register as Judge
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    <Tab label="Info" />
                    <Tab label="Workouts" />
                    <Tab label="Leaderboard" />
                    <Tab label="Schedule" />
                </Tabs>
            </Box>

            <DialogContent sx={{ px: { xs: 0, sm: 3 } }}>
                {activeTab === 0 && (
                    <CompetitionInfo
                        competition={competition}
                        isAdminView={false}
                    />
                )}
                {activeTab === 1 && (
                    <CompetitionWorkouts
                        competition={competition}
                        isAdminView={false}
                    />
                )}
                {activeTab === 2 && (
                    <CompetitionLeaderboard
                        competition={competition}
                        isAdminView={false}
                    />
                )}
                {activeTab === 3 && (
                    <CompetitionSchedule
                        competition={competition}
                        isAdminView={false}
                    />
                )}
            </DialogContent>

            {/* Register Dialog */}
            <RegisterCompetitionDialog
                open={openRegisterDialog}
                onClose={() => setOpenRegisterDialog(false)}
                competition={competition}
                userId={userId}
                onSuccess={handleRegisterSuccess}
            />

            {/* Submit Score Dialog */}
            {userRegistration && (
                <SubmitScoreDialog
                    open={openSubmitScoreDialog}
                    onClose={() => setOpenSubmitScoreDialog(false)}
                    competition={competition}
                    registration={userRegistration}
                    userId={userId}
                    onSuccess={handleSubmitScoreSuccess}
                />
            )}

            {/* Confirm Register as Judge Dialog */}
            <Dialog
                open={confirmJudgeDialogOpen}
                onClose={handleCancelRegisterAsJudge}
                aria-labelledby="confirm-judge-dialog-title"
                aria-describedby="confirm-judge-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="confirm-judge-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Registration as Judge
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="confirm-judge-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to register yourself as a judge for this competition? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleCancelRegisterAsJudge}
                        color="primary"
                        disabled={registeringAsJudge}
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRegisterAsJudge}
                        color="primary"
                        variant="contained"
                        autoFocus
                        disabled={registeringAsJudge}
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        {registeringAsJudge ? 'Registering...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}